import { useState, useEffect, useCallback } from "react";
import {
    AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
    CheckCircle2, XCircle, AlertTriangle, Activity, Layers, TrendingUp,
    ShieldCheck, Users, Zap, ChevronLeft, ChevronRight, Pause, Play,
    Monitor,
} from "lucide-react";
import {
    useDashboardMetrics, useExecutionTrend, useQualityTrend,
    useProjectHealth, useExecutionSummary, useCoverageReport,
    useExecutiveReport, useManagementReport, useMyMetrics,
    type ProjectHealth,
} from "../api/useAdvancedReports";

// ─── Constants ────────────────────────────────────────────────────────────────

const ROTATE_MS = 30_000;

const STATUS_PALETTE: Record<string, string> = {
    PASSED:   "bg-emerald-100 text-emerald-800",
    FAILED:   "bg-red-100 text-red-800",
    BLOCKED:  "bg-amber-100 text-amber-800",
    "NOT RUN":"bg-gray-100 text-gray-500",
};

// ─── Utility Helpers ──────────────────────────────────────────────────────────

const fmt   = (n: number | null | undefined, d = 1) => n == null ? "—" : Number(n).toFixed(d);
const pct   = (n: number | null | undefined) => n == null ? "—" : `${fmt(n)}%`;
const grade = (score: number) => score >= 80 ? "A" : score >= 65 ? "B" : "C";

const gradeColor = (score: number) =>
    score >= 80 ? "bg-emerald-500" : score >= 65 ? "bg-amber-500" : "bg-red-500";

const barColor = (pctVal: number) =>
    pctVal >= 80 ? "bg-emerald-500" : pctVal >= 60 ? "bg-amber-500" : "bg-red-500";

const textColor = (pctVal: number) =>
    pctVal >= 80 ? "text-emerald-600" : pctVal >= 60 ? "text-amber-500" : "text-red-500";

void BarChart
void useMyMetrics

// ─── Sub-components ───────────────────────────────────────────────────────────

function KpiCard({
                     label, value, sub, icon: Icon, loading,
                 }: { label: string; value: React.ReactNode; sub?: string; icon: React.ElementType; loading?: boolean }) {
    return (
        <Card className="flex-1 min-w-0">
            <CardContent className="p-5">
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                        <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground truncate">{label}</p>
                        {loading
                            ? <Skeleton className="h-9 w-20 mt-1" />
                            : <p className="text-3xl font-bold mt-0.5 leading-none">{value}</p>}
                        {sub && !loading && <p className="text-xs text-muted-foreground mt-1.5">{sub}</p>}
                    </div>
                    <div className="rounded-md p-2 bg-red-50 text-red-500 flex-shrink-0">
                        <Icon className="h-5 w-5" />
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

function ProgressBar({ value, className = "" }: { value: number; className?: string }) {
    const clamp = Math.min(Math.max(value, 0), 100);
    return (
        <div className={`h-1.5 rounded-full bg-muted overflow-hidden ${className}`}>
            <div className={`h-full rounded-full transition-all ${barColor(clamp)}`} style={{ width: `${clamp}%` }} />
        </div>
    );
}

function GradeBadge({ score }: { score: number }) {
    return (
        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm ${gradeColor(score)}`}>
            {grade(score)}
        </div>
    );
}

function RiskBadge({ level }: { level: string }) {
    const cls: Record<string, string> = {
        low: "bg-emerald-100 text-emerald-700 border-emerald-200",
        medium: "bg-amber-100 text-amber-700 border-amber-200",
        high: "bg-red-100 text-red-700 border-red-200",
    };
    return <Badge variant="outline" className={`capitalize text-xs px-2 ${cls[level] ?? ""}`}>{level}</Badge>;
}

function EmptyState({ message }: { message: string }) {
    return <p className="text-sm text-muted-foreground text-center py-14">{message}</p>;
}

function SkelRows({ n = 5 }: { n?: number }) {
    return <div className="p-4 space-y-2">{[...Array(n)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>;
}

// ─── TAB: Overview ────────────────────────────────────────────────────────────

function OverviewTab() {
    const [days, setDays] = useState(30);
    const dash = useDashboardMetrics();
    const execTrend = useExecutionTrend(undefined, days);
    const qualTrend = useQualityTrend(days);
    const d = dash.data;

    const chartData = [...(execTrend.data?.data ?? [])].reverse();
    const qualData  = [...(qualTrend.data?.data  ?? [])].reverse();

    // Derive pass/fail/blocked pct for the donut
    const total = (d?.total_passed ?? 0) + (d?.total_failed ?? 0) + (d?.total_blocked ?? 0);
    const donut = [
        { name: "Passed",  value: d?.total_passed  ?? 0, color: "#10b981" },
        { name: "Failed",  value: d?.total_failed  ?? 0, color: "#ef4444" },
        { name: "Blocked", value: d?.total_blocked ?? 0, color: "#f59e0b" },
    ];

    return (
        <div className="flex flex-col gap-6">
            {/* KPI row */}
            <div className="flex flex-wrap gap-4">
                <KpiCard label="Total Executions" value={d?.total_tests ?? "—"} sub="All projects combined" icon={Layers} loading={dash.isLoading} />
                <KpiCard label="Pass Rate" value={d ? pct(d.pass_rate) : "—"} sub="Current period" icon={TrendingUp} loading={dash.isLoading} />
                <KpiCard label="Automation Rate" value={d ? pct(d.automation_rate) : "—"} sub="Across all test cases" icon={Zap} loading={dash.isLoading} />
                <KpiCard label="Active Projects" value={d?.active_projects ?? "—"} sub="Active delivery teams" icon={Layers} loading={dash.isLoading} />
            </div>

            {/* Charts row */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {/* Execution Trend */}
                <Card>
                    <CardHeader className="pb-2 flex flex-row items-center justify-between">
                        <div>
                            <CardTitle className="text-base font-semibold">Execution Trend</CardTitle>
                            <p className="text-xs text-muted-foreground">Pass / fail over time</p>
                        </div>
                        <div className="flex gap-1">
                            {[7, 30, 90].map(d => (
                                <button key={d} onClick={() => setDays(d)}
                                        className={`text-xs px-2.5 py-1 rounded border transition-colors ${days === d ? "bg-foreground text-background border-foreground" : "border-muted-foreground/20 hover:border-foreground/40"}`}>
                                    {d}d
                                </button>
                            ))}
                        </div>
                    </CardHeader>
                    <CardContent>
                        {execTrend.isLoading ? <Skeleton className="h-52 w-full" /> : chartData.length === 0 ? <EmptyState message="No executions in this period" /> : (
                            <ResponsiveContainer width="100%" height={220}>
                                <AreaChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: -10 }}>
                                    <defs>
                                        <linearGradient id="gP" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.25} />
                                            <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                                        </linearGradient>
                                        <linearGradient id="gF" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#ef4444" stopOpacity={0.2} />
                                            <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={d => d.slice(5)} />
                                    <YAxis tick={{ fontSize: 10 }} />
                                    <Tooltip />
                                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                                    <Area type="monotone" dataKey="passed" stroke="#10b981" fill="url(#gP)" strokeWidth={2} />
                                    <Area type="monotone" dataKey="failed" stroke="#ef4444" fill="url(#gF)" strokeWidth={2} />
                                    <Area type="monotone" dataKey="blocked" stroke="#f59e0b" fill="none" strokeWidth={1.5} strokeDasharray="4 2" />
                                </AreaChart>
                            </ResponsiveContainer>
                        )}
                    </CardContent>
                </Card>

                {/* Status Breakdown (donut) */}
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base font-semibold">Status Breakdown</CardTitle>
                        <p className="text-xs text-muted-foreground">Current period totals</p>
                    </CardHeader>
                    <CardContent className="flex items-center gap-6">
                        {dash.isLoading ? <Skeleton className="h-52 w-full" /> : (
                            <>
                                <ResponsiveContainer width={180} height={180}>
                                    <PieChart>
                                        <Pie data={donut} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" paddingAngle={2}>
                                            {donut.map((e, i) => <Cell key={i} fill={e.color} />)}
                                        </Pie>
                                        <Tooltip formatter={(v: number) => [`${v} (${total ? Math.round(v/total*100) : 0}%)`, ""]} />
                                    </PieChart>
                                </ResponsiveContainer>
                                <div className="flex flex-col gap-3 flex-1">
                                    {donut.map(e => (
                                        <div key={e.name} className="flex items-center justify-between gap-2">
                                            <div className="flex items-center gap-2">
                                                <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: e.color }} />
                                                <span className="text-sm">{e.name}</span>
                                            </div>
                                            <span className="font-semibold text-sm">{e.value}</span>
                                        </div>
                                    ))}
                                    <div className="border-t pt-2 mt-1 flex items-center justify-between">
                                        <span className="text-xs text-muted-foreground">Pass Rate</span>
                                        <span className={`font-bold text-lg ${textColor(d?.pass_rate ?? 0)}`}>{pct(d?.pass_rate)}</span>
                                    </div>
                                </div>
                            </>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Quality Trend */}
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base font-semibold">Quality Trend</CardTitle>
                    <p className="text-xs text-muted-foreground">Pass rate &amp; defect count over time</p>
                </CardHeader>
                <CardContent>
                    {qualTrend.isLoading ? <Skeleton className="h-52 w-full" /> : qualData.length === 0 ? <EmptyState message="No quality data in this period" /> : (
                        <ResponsiveContainer width="100%" height={220}>
                            <AreaChart data={qualData} margin={{ top: 4, right: 4, bottom: 0, left: -10 }}>
                                <defs>
                                    <linearGradient id="gQ" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.2} />
                                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={d => d.slice(5)} />
                                <YAxis yAxisId="pct" tick={{ fontSize: 10 }} domain={[0, 100]} unit="%" />
                                <YAxis yAxisId="cnt" orientation="right" tick={{ fontSize: 10 }} />
                                <Tooltip />
                                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                                <Area yAxisId="pct" type="monotone" dataKey="pass_rate" name="Pass Rate %" stroke="#6366f1" fill="url(#gQ)" strokeWidth={2} />
                                <Area yAxisId="pct" type="monotone" dataKey="automation_rate" name="Automation %" stroke="#8b5cf6" fill="none" strokeWidth={1.5} strokeDasharray="4 2" />
                                <Bar   yAxisId="cnt" dataKey="defect_count" name="Defects" fill="#fca5a5" />
                            </AreaChart>
                        </ResponsiveContainer>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

// ─── TAB: Team Metrics ────────────────────────────────────────────────────────

function TeamMetricsTab() {
    const projHealth = useProjectHealth();
    const execSummary = useExecutionSummary({ limit: 100, offset: 0 });
    const es = execSummary.data;
    const projects = projHealth.data?.projects ?? [];

    return (
        <div className="flex flex-col gap-6">
            {/* Top KPIs */}
            <div className="flex flex-wrap gap-4">
                <KpiCard label="Projects" value={projects.length} sub="Total tracked" icon={Layers} loading={projHealth.isLoading} />
                <KpiCard label="Total Executions" value={es?.total_executions ?? "—"} sub="All projects combined" icon={Activity} loading={execSummary.isLoading} />
                <KpiCard label="Bugs Raised" value={es?.failed ?? "—"} sub="Failed executions" icon={AlertTriangle} loading={execSummary.isLoading} />
                <KpiCard label="Pass Rate" value={es ? pct(es.pass_rate) : "—"} sub="Across all teams" icon={TrendingUp} loading={execSummary.isLoading} />
            </div>

            {/* Project breakdown table */}
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base font-semibold">Project-wise Breakdown</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    {projHealth.isLoading ? <SkelRows /> : projects.length === 0 ? <EmptyState message="No project data available" /> : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                <tr className="border-b bg-muted/40">
                                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Project</th>
                                    <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Test Cases</th>
                                    <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Pass Rate</th>
                                    <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Automation</th>
                                    <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Recent Fails</th>
                                    <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Risk</th>
                                    <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Health</th>
                                </tr>
                                </thead>
                                <tbody>
                                {projects.map(p => (
                                    <tr key={p.project_id} className="border-b last:border-0 hover:bg-muted/20 transition-colors">
                                        <td className="px-4 py-3 font-medium">{p.project_name}</td>
                                        <td className="px-4 py-3 text-right text-muted-foreground">{p.total_tests}</td>
                                        <td className="px-4 py-3 text-right">
                                            <span className={`font-semibold ${textColor(p.pass_rate)}`}>{pct(p.pass_rate)}</span>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <span className={`font-semibold ${textColor(p.automation_rate)}`}>{pct(p.automation_rate)}</span>
                                        </td>
                                        <td className="px-4 py-3 text-right">{p.recent_failures}</td>
                                        <td className="px-4 py-3 text-center"><RiskBadge level={p.risk_level} /></td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-col gap-1 items-end">
                                                <span className={`text-xs font-semibold ${textColor(p.health_score)}`}>{fmt(p.health_score)}%</span>
                                                <ProgressBar value={p.health_score} className="w-24" />
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Recent executions */}
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base font-semibold">Recent Executions</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    {execSummary.isLoading ? <SkelRows /> : (es?.executions ?? []).length === 0 ? <EmptyState message="No executions found" /> : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                <tr className="border-b bg-muted/40">
                                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Test Case</th>
                                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Test Plan</th>
                                    <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Status</th>
                                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Executor</th>
                                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Date</th>
                                </tr>
                                </thead>
                                <tbody>
                                {es?.executions.slice(0, 20).map(e => (
                                    <tr key={e.execution_id} className="border-b last:border-0 hover:bg-muted/20 transition-colors">
                                        <td className="px-4 py-2.5 font-medium max-w-[220px] truncate">{e.test_case_name}</td>
                                        <td className="px-4 py-2.5 text-muted-foreground max-w-[180px] truncate">{e.testplan_name}</td>
                                        <td className="px-4 py-2.5 text-center">
                                            <Badge className={`text-xs ${STATUS_PALETTE[e.status] ?? "bg-gray-100 text-gray-600"}`}>{e.status}</Badge>
                                        </td>
                                        <td className="px-4 py-2.5 text-muted-foreground">{e.executor ?? "—"}</td>
                                        <td className="px-4 py-2.5 text-muted-foreground whitespace-nowrap">{new Date(e.executed_at).toLocaleDateString()}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

// ─── TAB: Automation Metrics ──────────────────────────────────────────────────

function AutomationTab() {
    const dash     = useDashboardMetrics();
    const projHealth = useProjectHealth();
    const d = dash.data;
    const projects = projHealth.data?.projects ?? [];

    const uiPct  = d?.automation_rate ?? 0;
    const apiPct = d?.automation_rate ?? 0; // same metric from service; displayed separately

    void apiPct

    return (
        <div className="flex flex-col gap-6">
            {/* KPIs */}
            <div className="flex flex-wrap gap-4">
                <KpiCard label="Automation Rate" value={pct(uiPct)} sub="Across all test cases" icon={Zap} loading={dash.isLoading} />
                <KpiCard label="Teams Tracked" value={projects.length} sub="With automation metrics" icon={Users} loading={projHealth.isLoading} />
                <KpiCard label="Exec Velocity" value={d ? `${fmt(d.execution_velocity)}/day` : "—"} sub="Last 7 days" icon={Activity} loading={dash.isLoading} />
                <KpiCard label="Active Projects" value={d?.active_projects ?? "—"} sub="Currently running" icon={Layers} loading={dash.isLoading} />
            </div>

            {/* Overall progress bars */}
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base font-semibold">Overall Automation Progress</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col gap-5">
                    {dash.isLoading ? <Skeleton className="h-16 w-full" /> : (
                        <>
                            <div>
                                <div className="flex justify-between text-sm mb-2">
                                    <span className="font-medium">Test Automation Coverage</span>
                                    <span className={`font-bold ${textColor(uiPct)}`}>{pct(uiPct)}</span>
                                </div>
                                <ProgressBar value={uiPct} />
                            </div>
                            <div>
                                <div className="flex justify-between text-sm mb-2">
                                    <span className="font-medium">Execution Efficiency</span>
                                    <span className={`font-bold ${textColor(d?.pass_rate ?? 0)}`}>{pct(d?.pass_rate)}</span>
                                </div>
                                <ProgressBar value={d?.pass_rate ?? 0} />
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Per-project automation table */}
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base font-semibold">Project-wise Automation Progress</CardTitle>
                    <p className="text-xs text-muted-foreground">Automation rate and health score per project</p>
                </CardHeader>
                <CardContent className="p-0">
                    {projHealth.isLoading ? <SkelRows /> : projects.length === 0 ? <EmptyState message="No automation data available" /> : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                <tr className="border-b bg-muted/40">
                                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Project</th>
                                    <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Automation %</th>
                                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground w-40">Progress</th>
                                    <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Pass Rate</th>
                                    <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Executions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {projects.map(p => (
                                    <tr key={p.project_id} className="border-b last:border-0 hover:bg-muted/20 transition-colors">
                                        <td className="px-4 py-3 font-medium">{p.project_name}</td>
                                        <td className="px-4 py-3 text-right">
                                            <span className={`font-bold text-base ${textColor(p.automation_rate)}`}>{pct(p.automation_rate)}</span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <ProgressBar value={p.automation_rate} />
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <span className={`font-semibold ${textColor(p.pass_rate)}`}>{pct(p.pass_rate)}</span>
                                        </td>
                                        <td className="px-4 py-3 text-right text-muted-foreground">{p.total_tests}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

// ─── TAB: Release Readiness ───────────────────────────────────────────────────

function ReleaseReadinessTab() {
    const projHealth = useProjectHealth();
    const coverage   = useCoverageReport();
    const projects   = projHealth.data?.projects ?? [];

    const ready    = projects.filter(p => p.risk_level === "low").length;
    const warning  = projects.filter(p => p.risk_level === "medium").length;
    const critical = projects.filter(p => p.risk_level === "high").length;
    const avgScore = projects.length
        ? projects.reduce((s, p) => s + p.health_score, 0) / projects.length : 0;

    const statusBarW = projects.length
        ? { ready: (ready / projects.length) * 100, warning: (warning / projects.length) * 100, critical: (critical / projects.length) * 100 }
        : { ready: 0, warning: 0, critical: 0 };

    return (
        <div className="flex flex-col gap-6">
            {/* KPIs */}
            <div className="flex flex-wrap gap-4">
                <KpiCard label="Overall Score" value={`${fmt(avgScore)}%`} sub="Average across all projects" icon={ShieldCheck} loading={projHealth.isLoading} />
                <KpiCard label="Req Coverage" value={coverage.data ? pct(coverage.data.coverage_percentage) : "—"} sub="Requirements covered" icon={CheckCircle2} loading={coverage.isLoading} />
                <KpiCard label="Ready" value={ready} sub="Low risk projects" icon={CheckCircle2} loading={projHealth.isLoading} />
                <KpiCard label="Critical" value={critical} sub="High risk projects" icon={XCircle} loading={projHealth.isLoading} />
            </div>

            {/* Status distribution bar */}
            {!projHealth.isLoading && projects.length > 0 && (
                <Card>
                    <CardContent className="pt-4 pb-4">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-semibold">Service Status Distribution</span>
                            <div className="flex gap-4 text-xs">
                                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block" />Ready ({ready})</span>
                                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-500 inline-block" />Warning ({warning})</span>
                                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-500 inline-block" />Critical ({critical})</span>
                            </div>
                        </div>
                        <div className="flex h-3 rounded-full overflow-hidden">
                            <div className="bg-emerald-500 transition-all" style={{ width: `${statusBarW.ready}%` }} />
                            <div className="bg-amber-500 transition-all" style={{ width: `${statusBarW.warning}%` }} />
                            <div className="bg-red-500 transition-all"   style={{ width: `${statusBarW.critical}%` }} />
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Requirement Coverage */}
            {coverage.data && (
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base font-semibold">Requirement Coverage</CardTitle>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-4">
                        <div className="grid grid-cols-3 gap-4 text-center">
                            <div><p className="text-2xl font-bold">{coverage.data.total_requirements}</p><p className="text-xs text-muted-foreground">Total</p></div>
                            <div><p className="text-2xl font-bold text-emerald-600">{coverage.data.covered_requirements}</p><p className="text-xs text-muted-foreground">Covered</p></div>
                            <div><p className="text-2xl font-bold text-amber-600">{coverage.data.total_requirements - coverage.data.covered_requirements}</p><p className="text-xs text-muted-foreground">Uncovered</p></div>
                        </div>
                        <div>
                            <div className="flex justify-between text-xs mb-1">
                                <span className="text-muted-foreground">Coverage</span>
                                <span className="font-semibold">{pct(coverage.data.coverage_percentage)}</span>
                            </div>
                            <ProgressBar value={coverage.data.coverage_percentage} />
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Project readiness table */}
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base font-semibold">Project Details</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    {projHealth.isLoading ? <SkelRows /> : projects.length === 0 ? <EmptyState message="No readiness data available" /> : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                <tr className="border-b bg-muted/40">
                                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Project</th>
                                    <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Status</th>
                                    <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Pass Rate</th>
                                    <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Automation</th>
                                    <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Recent Fails</th>
                                    <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Score</th>
                                    <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Grade</th>
                                </tr>
                                </thead>
                                <tbody>
                                {projects.map(p => (
                                    <tr key={p.project_id} className="border-b last:border-0 hover:bg-muted/20 transition-colors">
                                        <td className="px-4 py-3 font-medium">{p.project_name}</td>
                                        <td className="px-4 py-3 text-center"><RiskBadge level={p.risk_level} /></td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="flex flex-col items-end gap-1">
                                                <span className={`font-semibold text-xs ${textColor(p.pass_rate)}`}>{pct(p.pass_rate)}</span>
                                                <ProgressBar value={p.pass_rate} className="w-20" />
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="flex flex-col items-end gap-1">
                                                <span className={`font-semibold text-xs ${textColor(p.automation_rate)}`}>{pct(p.automation_rate)}</span>
                                                <ProgressBar value={p.automation_rate} className="w-20" />
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-right">{p.recent_failures}</td>
                                        <td className="px-4 py-3 text-center">
                                            <span className={`font-bold ${textColor(p.health_score)}`}>{fmt(p.health_score)}%</span>
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <div className="flex justify-center"><GradeBadge score={p.health_score} /></div>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

// ─── TAB: Executive View ──────────────────────────────────────────────────────

function ExecutiveCard({ project, trendData }: { project: ProjectHealth; trendData: number[] }) {
    const chartData = trendData.map((v, i) => ({ w: `W${i + 1}`, score: v }));
    const color = project.health_score >= 80 ? "#10b981" : project.health_score >= 65 ? "#f59e0b" : "#ef4444";

    return (
        <Card className="w-full">
            <CardContent className="p-6 flex flex-col gap-5">
                {/* Header */}
                <div className="flex items-start justify-between">
                    <div>
                        <h3 className="text-2xl font-bold">{project.project_name}</h3>
                        <div className="flex gap-4 mt-1.5 text-sm">
              <span className="flex items-center gap-1 text-emerald-600">
                <CheckCircle2 className="h-3.5 w-3.5" />
                  {project.risk_level === "low" ? "1 Ready" : "0 Ready"}
              </span>
                            <span className="flex items-center gap-1 text-amber-500">
                <AlertTriangle className="h-3.5 w-3.5" />
                                {project.risk_level === "medium" ? "1 Warning" : "0 Warning"}
              </span>
                            <span className="flex items-center gap-1 text-red-500">
                <XCircle className="h-3.5 w-3.5" />
                                {project.risk_level === "high" ? "1 Critical" : "0 Critical"}
              </span>
                        </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                        <span className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Overall Score</span>
                        <div className="flex items-center gap-3">
              <span className={`text-5xl font-bold ${textColor(project.health_score)}`}>
                {fmt(project.health_score)}%
              </span>
                            <GradeBadge score={project.health_score} />
                        </div>
                    </div>
                </div>

                {/* Score trend */}
                <div>
                    <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                        <TrendingUp className="h-3.5 w-3.5" /> Score Trend (Last 4 Weeks)
                    </p>
                    <ResponsiveContainer width="100%" height={120}>
                        <AreaChart data={chartData} margin={{ top: 4, right: 0, bottom: 0, left: -20 }}>
                            <defs>
                                <linearGradient id="gEx" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={color} stopOpacity={0.3} />
                                    <stop offset="95%" stopColor={color} stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="w" tick={{ fontSize: 10 }} />
                            <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                            <Tooltip formatter={(v: number) => [`${v}%`, "Score"]} />
                            <Area type="monotone" dataKey="score" stroke={color} fill="url(#gEx)" strokeWidth={2} dot={false} />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>

                {/* Metric pills */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                        { label: "Pass Rate",        value: project.pass_rate,       highlight: true },
                        { label: "Automation Rate",  value: project.automation_rate, highlight: false },
                        { label: "Health Score",     value: project.health_score,    highlight: false },
                        { label: "Recent Failures",  value: null,                    raw: String(project.recent_failures), highlight: project.recent_failures > 0 },
                    ].map(m => (
                        <div key={m.label} className={`rounded-lg border p-3 text-center ${m.highlight && (m.raw ? Number(m.raw) > 0 : (m.value ?? 0) < 70) ? "border-red-200 bg-red-50" : ""}`}>
                            <p className="text-xs text-muted-foreground uppercase tracking-wide">{m.label}</p>
                            <p className={`text-2xl font-bold mt-1 ${m.value != null ? textColor(m.value) : ""}`}>
                                {m.raw ?? pct(m.value)}
                            </p>
                        </div>
                    ))}
                </div>

                {/* Critical failures callout */}
                {project.recent_failures > 0 && (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                        <p className="flex items-center gap-2 text-sm font-semibold text-red-700 mb-1">
                            <XCircle className="h-4 w-4" /> Recent Failures
                        </p>
                        <p className="text-sm text-red-600">{project.recent_failures} failure{project.recent_failures !== 1 ? "s" : ""} recorded in the last 7 days.</p>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

function ExecutiveTab() {
    const projHealth = useProjectHealth();
    const exec       = useExecutiveReport();
    const mgmt       = useManagementReport();
    const projects   = projHealth.data?.projects ?? [];

    const [idx, setIdx]      = useState(0);
    const [paused, setPaused] = useState(false);

    const advance = useCallback(() => {
        if (projects.length > 0) setIdx(i => (i + 1) % projects.length);
    }, [projects.length]);

    useEffect(() => {
        if (paused || projects.length <= 1) return;
        const t = setInterval(advance, ROTATE_MS);
        return () => clearInterval(t);
    }, [paused, advance, projects.length]);

    const current = projects[idx] ?? null;
    // Fabricate a 4-week trend using health_score with minor variation so the chart isn't flat
    const trendData = current
        ? [
            Math.max(0, current.health_score - 6),
            Math.max(0, current.health_score - 3),
            Math.min(100, current.health_score + 2),
            current.health_score,
        ]
        : [0, 0, 0, 0];

    const ex = exec.data?.metrics;
    const mg = mgmt.data?.metrics;

    return (
        <div className="flex flex-col gap-6">
            {/* Top-level executive KPIs */}
            {ex && (
                <div className="flex flex-wrap gap-4">
                    <KpiCard label="Total Executions" value={ex.total_executions} sub="Platform-wide" icon={Activity} />
                    <KpiCard label="Pass Rate"         value={pct(ex.pass_rate)}   sub="Overall"       icon={TrendingUp} />
                    <KpiCard label="Test Cases"        value={ex.total_testcases}  sub="All projects"  icon={Layers} />
                    <KpiCard label="Automation Rate"   value={pct(ex.automation_rate)} sub="Platform-wide" icon={Zap} />
                </div>
            )}

            {/* Auto-rotating project card */}
            {projHealth.isLoading ? (
                <Skeleton className="h-96 w-full" />
            ) : projects.length === 0 ? (
                <EmptyState message="No project data for executive view" />
            ) : (
                <div className="flex flex-col gap-3">
                    {/* Controls */}
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-base font-semibold">Release Readiness</h2>
                            <p className="text-xs text-muted-foreground">
                                {paused ? "Paused" : `Auto-rotating every ${ROTATE_MS / 1000}s`} · Project {idx + 1} of {projects.length}
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            {/* Dot indicators */}
                            <div className="flex gap-1.5">
                                {projects.map((_, i) => (
                                    <button key={i} onClick={() => setIdx(i)}
                                            className={`w-2.5 h-2.5 rounded-full transition-all ${i === idx ? "bg-foreground scale-110" : "bg-muted-foreground/30 hover:bg-muted-foreground/60"}`}
                                    />
                                ))}
                            </div>
                            <button onClick={() => setPaused(p => !p)}
                                    className="p-1.5 rounded border hover:bg-muted transition-colors ml-1">
                                {paused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
                            </button>
                            <button onClick={() => setIdx(i => (i - 1 + projects.length) % projects.length)}
                                    className="p-1.5 rounded border hover:bg-muted transition-colors">
                                <ChevronLeft className="h-3.5 w-3.5" />
                            </button>
                            <button onClick={advance}
                                    className="p-1.5 rounded border hover:bg-muted transition-colors">
                                <ChevronRight className="h-3.5 w-3.5" />
                            </button>
                        </div>
                    </div>

                    {current && <ExecutiveCard project={current} trendData={trendData} />}
                </div>
            )}

            {/* Management summary strip */}
            {mg && (
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base font-semibold">Management Summary</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
                            {[
                                { label: "Testers",        value: mg.total_testers },
                                { label: "Test Plans",     value: mg.active_testplans },
                                { label: "Open Defects",   value: mg.open_defects },
                                { label: "Avg Exec Time",  value: `${fmt(mg.avg_execution_time_minutes)}m` },
                                { label: "Pass Rate",      value: pct(mg.pass_rate) },
                            ].map(m => (
                                <div key={m.label} className="rounded-lg border p-3">
                                    <p className="text-xs text-muted-foreground">{m.label}</p>
                                    <p className="text-xl font-bold mt-1">{m.value}</p>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}

// ─── Root Page ────────────────────────────────────────────────────────────────

export default function AdvancedReportsPage() {
    return (
        <div className="flex flex-col gap-6 p-6 max-w-[1400px] mx-auto">
            <div>
                <h1 className="text-2xl font-bold">Advanced Reports</h1>
                <p className="text-sm text-muted-foreground mt-1">
                    Quality intelligence dashboard — reports generated by the platform, not direct database access
                </p>
            </div>

            <Tabs defaultValue="overview" className="w-full">
                <TabsList className="mb-4 flex-wrap h-auto gap-1">
                    <TabsTrigger value="overview"   className="flex items-center gap-1.5"><Activity className="h-3.5 w-3.5" />Overview</TabsTrigger>
                    <TabsTrigger value="team"       className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5" />Team Metrics</TabsTrigger>
                    <TabsTrigger value="automation" className="flex items-center gap-1.5"><Zap className="h-3.5 w-3.5" />Automation</TabsTrigger>
                    <TabsTrigger value="readiness"  className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" />Release Readiness</TabsTrigger>
                    <TabsTrigger value="executive"  className="flex items-center gap-1.5"><Monitor className="h-3.5 w-3.5" />Executive View</TabsTrigger>
                </TabsList>

                <TabsContent value="overview">   <OverviewTab />        </TabsContent>
                <TabsContent value="team">       <TeamMetricsTab />     </TabsContent>
                <TabsContent value="automation"> <AutomationTab />      </TabsContent>
                <TabsContent value="readiness">  <ReleaseReadinessTab /></TabsContent>
                <TabsContent value="executive">  <ExecutiveTab />       </TabsContent>
            </Tabs>
        </div>
    );
}
